import React, { useEffect, useState } from "react";
import { Tag as TagIcon, Check } from "lucide-react";


import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import { cn } from "../../lib/utils";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { canalDoTicket } from "../../helpers/canalDoTicket";
import { esperaDoTicket, useTickDeEspera } from "../../helpers/tempoDeEspera";
import SeloDeEncerramento from "../SeloDeEncerramento";

// Quem está com o atendimento, em português. Duas linhas bastam: o nome do
// contato e uma frase curta — nada de rótulos técnicos para decifrar.
const TicketInfo = ({ contact, ticket, onClick }) => {
  // O contato publicou status nas últimas 24h?
  //
  // É o sinal que o WhatsApp já ensinou a ler — anel no avatar — e ele precisa
  // existir ANTES de alguém abrir o painel, senão não sinaliza nada. Custa uma
  // consulta por atendimento aberto, e ela é pequena de propósito: a lista não
  // traz mídia, só a marca de que existe.
  const [temStatus, setTemStatus] = useState(false);
  const contactId = contact?.id;

  useEffect(() => {
    if (!contactId) return undefined;

    let valeAinda = true;

    // Falha silenciosa: sem resposta, o anel simplesmente não aparece. Ele é
    // uma pista, não um dado do atendimento.
    api
      .get(`/status-recebidos/contato/${contactId}`)
      .then(({ data }) => {
        if (valeAinda) setTemStatus(Boolean(data?.length));
      })
      .catch(() => setTemStatus(false));

    return () => {
      valeAinda = false;
    };
  }, [contactId]);

  const initials = (contact?.name || "?")
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase();

  const canal = canalDoTicket(ticket);
  const CanalIcone = canal.icone;
  // Sem o tick o cabeçalho congelava no tempo do primeiro render enquanto a
  // lista continuava contando — mesmo atendimento, dois tempos diferentes.
  useTickDeEspera();
  const espera = esperaDoTicket(ticket);

  return (
    <button
      type="button"
      onClick={onClick}
      title="Ver dados do contato"
      className="flex min-w-0 flex-1 items-center gap-2.5 rounded-lg py-1 pl-1 pr-2.5 text-left transition-colors hover:bg-accent"
    >
      <span className="relative shrink-0">
        <Avatar
          className={cn(
            "h-10 w-10",
            temStatus && "ring-2 ring-primary ring-offset-1 ring-offset-background"
          )}
          title={
            temStatus
              ? "Este contato publicou status nas últimas 24h"
              : undefined
          }
        >
          <AvatarImage src={contact?.profilePicUrl} alt={contact?.name} />
          <AvatarFallback>{initials}</AvatarFallback>
        </Avatar>
        <span
          title={canal.rotulo}
          aria-label={`Canal: ${canal.rotulo}`}
          className={cn(
            "absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full ring-2 ring-background",
            canal.classe,
          )}
        >
          <CanalIcone className="h-2.5 w-2.5" />
        </span>
      </span>

      <div className="min-w-0">
        <p className="flex min-w-0 items-baseline gap-1.5 truncate text-[15px] font-semibold leading-tight">
          <span className="truncate">{contact?.name}</span>
          <span className="shrink-0 font-normal text-muted-foreground">
            #{ticket.id}
          </span>
        </p>

        <p className="mt-0.5 flex min-w-0 items-center gap-1.5 truncate text-xs text-muted-foreground">
          {/* O nome da rede por extenso, não só o selo no avatar: com
              Instagram, Telegram, Messenger e X na mesma lista, um ícone de
              4px não diz para onde a resposta vai sair. */}
          <span
            className={cn(
              "shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide",
              canal.classePill,
            )}
          >
            {canal.rotulo}
          </span>

          {/* Encerrado: o motivo vale mais do que quem atendeu. É a primeira
              coisa que se quer saber ao abrir uma conversa da aba Resolvidos —
              e até agora não aparecia em lugar nenhum. */}
          {ticket.status === "closed" ? (
            <SeloDeEncerramento ticket={ticket} mostrarObservacao />
          ) : ticket.user?.name ? (
            <span className="truncate">Atribuído à: {ticket.user.name}</span>
          ) : (
            <span className="truncate">Ninguém assumiu ainda</span>
          )}

          {/* A espera é o único ponto de cor da linha: é o que aperta. */}
          {espera && (
            <>
              <span className="text-muted-foreground/50">·</span>
              <span className={cn("shrink-0 font-medium", espera.classe)}>
                {ticket.status === "pending"
                  ? "aguardando há "
                  : "sem resposta há "}
                {espera.rotulo}
              </span>
            </>
          )}
        </p>
      </div>
    </button>
  );
};

// Etiquetas: um ícone com a quantidade, e o resto só ao abrir.
//
// Antes era uma faixa fixa abaixo do cabeçalho, com os nomes das etiquetas
// sempre à mostra — comia uma linha inteira da conversa para uma informação
// que quase nunca muda. Agora é ícone; quem quer ver ou mexer, clica.
//
// O MESMO botão aplica as duas etiquetas que o sistema tem: a da PESSOA (vale
// para todas as conversas dela, é o que a régua de follow-up escuta) e a da
// CONVERSA (morre com o atendimento, é o que relatório e campanha contam).
// Antes só a da conversa cabia aqui, e marcar a pessoa eram quatro passos por
// outro caminho — abrir a ficha, "Editar contato", mexer num multi-select,
// salvar. Por isso ninguém usava.
//
// Uma linha por etiqueta, com uma caixa para cada alvo: o nome não se repete,
// dá para ver de relance onde cada uma está aplicada, e o cabeçalho da coluna
// diz o que o clique faz — a explicação fica no lugar do clique, não num
// treinamento.
export const TicketTagsBar = ({ ticket, contact }) => {
  const [aberto, setAberto] = useState(false);
  const [todas, setTodas] = useState([]);
  // Estado local para o clique responder na hora; o servidor confirma depois.
  const [naConversa, setNaConversa] = useState(() => new Set());
  const [naPessoa, setNaPessoa] = useState(() => new Set());

  const etiquetas = ticket.tags || [];
  const daPessoa = contact?.tags || [];
  const total = etiquetas.length + daPessoa.length;

  useEffect(() => {
    setNaConversa(new Set(etiquetas.map((t) => t.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket.id, ticket.tags]);

  useEffect(() => {
    setNaPessoa(new Set(daPessoa.map((t) => t.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact?.id, contact?.tags]);

  // A lista completa só é buscada quando o modal abre — é o único momento em
  // que ela serve, e o ícone aparece em toda conversa.
  useEffect(() => {
    if (!aberto) return;

    (async () => {
      try {
        const { data } = await api.get("/tags");
        setTodas(data?.tags || data || []);
      } catch (err) {
        toastError(err);
      }
    })();
  }, [aberto]);

  const alternar = async (tagId, alvo) => {
    const atual = alvo === "pessoa" ? naPessoa : naConversa;
    const aplicar = alvo === "pessoa" ? setNaPessoa : setNaConversa;
    const tinha = atual.has(tagId);
    const novo = new Set(atual);

    if (tinha) novo.delete(tagId);
    else novo.add(tagId);

    aplicar(novo);

    try {
      if (alvo === "pessoa") {
        // Soma ou tira UMA etiqueta, em vez de mandar o conjunto inteiro.
        //
        // O conjunto inteiro é o contrato do "Editar contato", onde a tela
        // acabou de carregar a lista completa. Aqui não: se as etiquetas da
        // pessoa ainda não tivessem chegado, um clique mandaria uma lista de
        // um item e apagaria em silêncio tudo o que ela já tinha. Somar uma
        // também é o certo quando dois atendentes mexem na mesma pessoa ao
        // mesmo tempo.
        await api.post("/contacts/bulk-tag", {
          contactIds: [contact.id],
          tagId,
          acao: tinha ? "remover" : "adicionar",
        });
      } else {
        await api.put(`/tickets/${ticket.id}/tags`, { tagIds: [...novo] });
      }
    } catch (err) {
      // Desfaz o que a tela já mostrou: senão fica dizendo que aplicou algo
      // que o servidor recusou.
      aplicar(atual);
      toastError(err);
    }
  };

  const Caixa = ({ marcada, aoClicar, rotulo, desabilitada }) => (
    <button
      type="button"
      role="checkbox"
      aria-checked={marcada}
      aria-label={rotulo}
      title={rotulo}
      disabled={desabilitada}
      onClick={aoClicar}
      className={cn(
        "flex h-8 w-16 shrink-0 items-center justify-center rounded-md transition-colors",
        marcada ? "text-primary" : "text-muted-foreground/40",
        desabilitada ? "cursor-not-allowed opacity-40" : "hover:bg-accent",
      )}
    >
      <span
        className={cn(
          "flex h-4 w-4 items-center justify-center rounded-[4px] border",
          marcada ? "border-primary bg-primary" : "border-current",
        )}
      >
        {marcada && <Check className="h-3 w-3 text-primary-foreground" />}
      </span>
    </button>
  );

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        // Mais um ícone da barra do cabeçalho, e não um botão flutuante: ele
        // ficava sobre a foto do contato, cobrindo um quarto dela, no mesmo
        // avatar de 40px que já carrega o selo do canal e vai receber o anel
        // de status. Sem sombra e sem borda — aqui não há nada por trás de
        // que se destacar.
        className="relative shrink-0"
        title={
          total
            ? [
                daPessoa.length &&
                  `Da pessoa: ${daPessoa.map((t) => t.name).join(", ")}`,
                etiquetas.length &&
                  `Desta conversa: ${etiquetas.map((t) => t.name).join(", ")}`,
              ]
                .filter(Boolean)
                .join(" · ")
            : "Sem etiquetas"
        }
        aria-label="Etiquetas"
        onClick={() => setAberto(true)}
      >
        <TagIcon className="h-4 w-4" />
        {total > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium text-primary-foreground">
            {total}
          </span>
        )}
      </Button>

      <Dialog open={aberto} onOpenChange={setAberto}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Etiquetas</DialogTitle>
            <p className="text-sm text-muted-foreground">
              Marque onde cada etiqueta vale: na pessoa, nesta conversa, ou nas
              duas.
            </p>
          </DialogHeader>

          {todas.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Nenhuma etiqueta cadastrada ainda.
            </p>
          ) : (
            <>
              {/* O cabeçalho das colunas é a explicação inteira: quem lê
                  "sempre" e "só aqui" entende a diferença sem precisar saber
                  que existem dois tipos de etiqueta. */}
              <div className="flex items-end gap-2 border-b pb-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                <span className="min-w-0 flex-1" />
                <span className="w-16 shrink-0 text-center text-[10px] leading-tight">
                  Pessoa
                  <span className="block normal-case tracking-normal opacity-70">
                    sempre
                  </span>
                </span>
                <span className="w-16 shrink-0 text-center text-[10px] leading-tight">
                  Conversa
                  <span className="block normal-case tracking-normal opacity-70">
                    só aqui
                  </span>
                </span>
              </div>

              <div className="-mx-1 max-h-72 space-y-0.5 overflow-y-auto px-1">
                {todas.map((tag) => (
                  <div
                    key={tag.id}
                    className="flex items-center gap-2 rounded-lg py-0.5 pl-2.5 pr-0 text-sm"
                  >
                    <span
                      className="h-3 w-3 shrink-0 rounded-full ring-1 ring-black/10"
                      style={{ backgroundColor: tag.color }}
                    />
                    <span className="min-w-0 flex-1 truncate">{tag.name}</span>
                    <Caixa
                      marcada={naPessoa.has(tag.id)}
                      // Sem o contato carregado não há em quem aplicar: o
                      // clique iria para um id indefinido.
                      desabilitada={!contact?.id}
                      rotulo={`"${tag.name}" na pessoa — vale para todas as conversas dela`}
                      aoClicar={() => alternar(tag.id, "pessoa")}
                    />
                    <Caixa
                      marcada={naConversa.has(tag.id)}
                      rotulo={`"${tag.name}" só nesta conversa`}
                      aoClicar={() => alternar(tag.id, "conversa")}
                    />
                  </div>
                ))}
              </div>
            </>
          )}

          <p className="text-xs text-muted-foreground">
            Vale na hora: não há botão de salvar.
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TicketInfo;
